import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import AuthLayout from '../components/AuthLayout';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('auth.invalidResetToken'));
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(t('auth.invalidResetToken'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', { token, newPassword: password });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.resetPasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout title={t('auth.resetPasswordTitle')} subtitle={t('auth.invalidResetToken')}>
        <div className="text-center">
          <div className="gp-alert-error" role="alert">
            {t('auth.invalidResetToken')}
          </div>
          <Link to="/forgot-password" className="gp-link">
            {t('auth.requestNewResetLink')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.resetPasswordTitle')} subtitle={t('auth.newPassword')}>
      {success ? (
        <div className="text-center">
          <div
            className="mb-4 p-3 rounded-md text-sm"
            style={{
              background: 'var(--gp-success-soft)',
              border: '1px solid var(--gp-success)',
              color: 'var(--gp-success)',
            }}
            role="status"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="text-lg mb-1" aria-hidden="true" />
            <p>{t('auth.resetPasswordSuccess')}</p>
          </div>
          <p className="text-sm gp-text-muted">{t('auth.redirectingToLogin')}</p>
        </div>
      ) : (
        <>
          {error && (
            <div className="gp-alert-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2" style={{ color: 'var(--gp-ink)' }}>
                {t('auth.newPassword')}
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faLock}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted"
                  aria-hidden="true"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder={t('auth.newPasswordPlaceholder')}
                  className="input-field pl-10 pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 gp-text-muted"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--gp-ink)' }}
              >
                {t('auth.confirmNewPassword')}
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faLock}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted"
                  aria-hidden="true"
                />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  className="input-field pl-10 pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 gp-text-muted"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <button type="submit" className="w-full gp-btn-primary py-3" disabled={loading}>
              {loading ? (
                <>
                  <span className="gp-spinner !w-4 !h-4 !mx-0 border" aria-hidden="true" />
                  <span>{t('auth.resettingPassword')}</span>
                </>
              ) : (
                <span>{t('auth.resetPassword')}</span>
              )}
            </button>
          </form>

          <div className="mt-5 text-center">
            <Link to="/login" className="gp-link text-sm">
              {t('auth.backToLogin')}
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
